import * as Yup from "yup";
import { FieldArray, FormikProvider } from "formik";
import CustomInputText from "../../components/forms/CustomInputText";
import CustomDropdown from "../../components/forms/CustomDropdown";
import AbstractNomenclature from "../nomenclatures/common/AbstractNomenclature";
import { ScheduleResponse, ScheduleSubjectResponse } from "../../models/dtos";
import { subjectsApi } from "../../services/nomenclatures/subjectsApi";
import { teachersApi } from "../../services/teachers/teachersApi";
import ItemBrowser from "../../components/forms/ItemBrowser";
import { groupsApi } from "../../services/nomenclatures/groupsApi";
import { activityTypesApi } from "../../services/nomenclatures/activityTypesApi";
import { hallsApi } from "../../services/nomenclatures/hallsApi";
import { scheduleSubjectsApi } from "../../services/scheduleSubjects/scheduleSubjectsApi";
import { Button } from "primereact/button";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { Divider } from "primereact/divider";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import CustomInputMask from "../../components/forms/CustomInputMask";

const weekDayToNum = [
  { label: "Понеделник", value: 1 },
  { label: "Вторник", value: 2 },
  { label: "Сряда", value: 3 },
  { label: "Четвъртък", value: 4 },
  { label: "Петък", value: 5 },
  { label: "Събота", value: 6 },
  { label: "Неделя", value: 7 },
];

const ScheduleSubjectsList = ({
  schedule,
  subjects,
  specialityId,
  sessionTypeId,
}: {
  schedule: ScheduleResponse;
  subjects: ScheduleSubjectResponse[];
  specialityId: number;
  sessionTypeId: number;
}) => {
  const [triggerGetSubjects, { isLoading: isGetSubjectsLoading }] = subjectsApi.useLazyGetSubjectsBySpecialityIdQuery();
  const [triggerGetGroups, { isLoading: isGetGroupsLoading }] =
    groupsApi.useLazyGetGroupsBySpecialityIdAndAcademicYearQuery();
  const { data: activityTypes } = activityTypesApi.useGetActivityTypesQuery();
  const { data: halls } = hallsApi.useGetHallsQuery();
  const { data: teachers } = teachersApi.useGetTeachersQuery({});

  const validationSchema = Yup.object().shape({
    scheduleId: Yup.number().required("Полето е задължително."),
    scheduleSubjectId: Yup.number().nullable(),
    subjectId: Yup.number().required("Полето е задължително."),
    subjectName: Yup.string().nullable(),
    groupId: Yup.number().nullable(),
    groupName: Yup.string().nullable(),
    subgroup: Yup.string().nullable(),
    activityTypeId: Yup.number().nullable(),
    activityTypeName: Yup.string().nullable(),
    hallId: Yup.number().nullable(),
    hallNumber: Yup.string().nullable(),
    comment: Yup.string().nullable(),
    teacherTeams: Yup.array()
      .of(
        Yup.object().shape({
          teacherId: Yup.number().required("Изберете преподавател."),
          orderNumber: Yup.number().nullable(),
        }),
      )
      .min(1, "Добавете поне един преподавател."),
    subjectTimeTable: Yup.array().of(
      Yup.object().shape({
        startDate: Yup.string().nullable(),
        endDate: Yup.string().nullable(),
        startWeek: Yup.number().nullable(),
        endWeek: Yup.number().nullable(),
        weekDay: Yup.number().nullable(),
        startHour: Yup.string().nullable(),
        endHour: Yup.string().nullable(),
        evenWeek: Yup.boolean().nullable(),
      }),
    ),
  });

  const editDialog = {
    renderForm: (formik: any) => (
      <FormikProvider value={formik}>
        {formik.values.scheduleSubjectId && <CustomInputText label="ID" id="scheduleSubjectId" disabled />}
        <CustomInputText label="Разписание" id="scheduleId" disabled />
        <ItemBrowser
          id="subjectId"
          label="Дисциплина"
          optionsPromise={() => triggerGetSubjects(specialityId)}
          loading={isGetSubjectsLoading}
          idField="subjectId"
          labelField="name"
          currentLabelText={formik?.values.subjectName}
          columns={[
            { field: "subjectId", header: "ID", sortable: true, filter: true },
            { field: "name", header: "Име", sortable: true, filter: true },
          ]}
        />
        <ItemBrowser
          id="groupId"
          label="Група"
          optionsPromise={() => triggerGetGroups({ specialityId: schedule?.specialityId!, academicYearId: schedule?.academicYearId! })}
          loading={isGetGroupsLoading}
          idField="groupId"
          labelField="name"
          currentLabelText={formik?.values.groupName}
          columns={[
            { field: "groupId", header: "ID", sortable: true, filter: true },
            { field: "name", header: "Номер на група", sortable: true, filter: true },
          ]}
        />
        <CustomInputText label="Подгрупа" id="subgroup" type="text" />
        {sessionTypeId < 1 && (
          <CustomDropdown
            id="activityTypeId"
            label="Тип на дисциплината"
            options={activityTypes?.data || []}
            optionLabel="name"
            optionValue="activityTypeId"
          />
        )}
        <CustomDropdown
          id="hallId"
          label="Зала"
          options={halls?.data || []}
          optionLabel="hallNumber"
          optionValue="hallId"
          showClear
        />

        <FieldArray name="teacherTeams">
          {({ push, remove }) => (
            <div>
              <label className="p-mb-3 font-bold">Преподаватели</label>

              {formik.values.teacherTeams.map((_team: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: "4px 0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ flex: "2", minWidth: "250px" }}>
                      <CustomDropdown
                        id={`teacherTeams.${index}.teacherId`}
                        label={`Преподавател ${index + 1}`}
                        filter
                        options={teachers?.data || []}
                        optionLabel={(teacher) =>
                          `${teacher.firstName} ${teacher.lastName}, ${teacher.academicPositionShortName ?? ""} ${teacher.academicRankShortName ?? ""} - ${teacher.departmentFacultyShortName ?? ""} (${teacher.teacherId})`
                        }
                        optionValue="teacherId"
                      />
                    </div>

                    <div style={{ flex: "0.5", minWidth: "70px" }}>
                      <CustomInputText id={`teacherTeams.${index}.orderNumber`} label="Поредност" type="number" />
                    </div>

                    <div className="mt-3">
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger"
                        type="button"
                        onClick={() => remove(index)}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button
                type="button"
                icon="pi pi-plus"
                label="Добави преподавател"
                onClick={() =>
                  push({
                    teacherId: "",
                    orderNumber: formik.values.teacherTeams.length + 1,
                  })
                }
                className="p-button-secondary mt-3"
              />
            </div>
          )}
        </FieldArray>
        <Divider />
        <FieldArray name="subjectTimeTable">
          {({ push, remove }) => (
            <div className="mt-10">
              <label className="p-mb-3 font-bold">График</label>

              {formik.values.subjectTimeTable.map((_timetable: any, index: number) => (
                <div
                  key={index}
                  style={{
                    padding: "10px 0",
                  }}
                >
                  <div style={{ display: "flex", gap: "20px" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                      <CustomDatepicker id={`subjectTimeTable.${index}.startDate`} label="Начална дата" showButtonBar />
                      {sessionTypeId < 1 && (
                        <CustomDatepicker id={`subjectTimeTable.${index}.endDate`} label="Крайна дата" />
                      )}
                    </div>

                    {sessionTypeId < 1 && (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                        <CustomInputText
                          id={`subjectTimeTable.${index}.startWeek`}
                          label="Начална седмица"
                          type="number"
                        />
                        <CustomInputText
                          id={`subjectTimeTable.${index}.endWeek`}
                          label="Крайна седмица"
                          type="number"
                        />
                        <div className="flex flex-row gap-2 align-items-center">
                          <TriStateCheckbox
                            value={formik.values.subjectTimeTable[index].evenWeek}
                            onChange={(e) => formik.setFieldValue(`subjectTimeTable.${index}.evenWeek`, e.value)}
                          />
                          <label className="ml-2">
                            Четност:{" "}
                            {formik.values.subjectTimeTable[index].evenWeek === true
                              ? "четни"
                              : formik.values.subjectTimeTable[index].evenWeek === false
                                ? "нечетни"
                                : "всички"}{" "}
                            седмици
                          </label>
                        </div>
                      </div>
                    )}

                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                      <CustomDropdown
                        id={`subjectTimeTable.${index}.weekDay`}
                        label="Ден от седмицата"
                        options={weekDayToNum}
                        optionLabel="label"
                        optionValue="value"
                        showClear
                      />
                      <CustomInputMask
                        id={`subjectTimeTable.${index}.startHour`}
                        label="Начален час"
                        mask="99:99"
                        placeholder="напр. 10:30"
                      />
                      <CustomInputMask
                        id={`subjectTimeTable.${index}.endHour`}
                        label="Краен час"
                        mask="99:99"
                        placeholder="напр. 15:30"
                      />
                    </div>

                    <div style={{ flex: 0.5, display: "flex", alignItems: "flex-start", paddingTop: "25px" }}>
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger"
                        type="button"
                        onClick={() => remove(index)}
                        disabled={formik.values.subjectTimeTable.length === 1}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <CustomInputText id={`subjectTimeTable.${index}.comment`} label="Коментар" type="multiline" />
                  </div>
                </div>
              ))}

              <Button
                type="button"
                icon="pi pi-plus"
                label="Добави график"
                onClick={() =>
                  push({
                    startDate: "",
                    endDate: "",
                    startWeek: null,
                    endWeek: null,
                    weekDay: null,
                    startHour: "",
                    endHour: "",
                    evenWeek: null,
                  })
                }
                className="p-button-secondary mt-3"
              />
            </div>
          )}
        </FieldArray>
      </FormikProvider>
    ),
    validationSchema,
    item: {
      scheduleId: schedule.scheduleId,
      teacherTeams: [{ teacherId: "", orderNumber: null }],
      subjectTimeTable: [
        {
          startDate: null,
          endDate: null,
          startWeek: null,
          endWeek: null,
          weekDay: null,
          startHour: null,
          endHour: null,
          evenWeek: null,
        },
      ],
    },
  };

  return (
    <div>
      <h3 className="text-xl font-light mb-4">Разписание на дисциплините:</h3>

      {subjects && (
        <AbstractNomenclature
          api={{
            useGetListQuery: {
              data: { data: subjects },
              isLoading: false,
              isError: false,
            },
            useUpdateItemMutation: scheduleSubjectsApi.useUpdateScheduleSubjectMutation,
            useDeleteItemMutation: scheduleSubjectsApi.useDeleteScheduleSubjectMutation,
          }}
          columns={[
            { field: "scheduleSubjectId", header: "ID", sortable: true, filter: true },
            { field: "subjectName", header: "Дисциплина", sortable: true, filter: true },
            {
              expression: (row: ScheduleSubjectResponse) =>
                `${row.teacherTeams
                  ?.slice()
                  .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
                  .map((teacher) => teacher.teacherName)
                  .join(", ")}`,
              header: "Преподаватели",
              sortable: true,
              filter: true,
            },
            { field: "groupName" as keyof ScheduleSubjectResponse, header: "Група", sortable: true, filter: true },
            { field: "subgroup" as keyof ScheduleSubjectResponse, header: "Подгрупа", sortable: true, filter: true },
            ...(sessionTypeId && sessionTypeId > 0
              ? []
              : [
                  {
                    field: "activityTypeName" as keyof ScheduleSubjectResponse,
                    header: "Тип на дисциплината",
                    sortable: true,
                    filter: true,
                  },
                ]),
            { field: "hallNumber" as keyof ScheduleSubjectResponse, header: "Зала", sortable: true, filter: true },
          ]}
          idField="scheduleSubjectId"
          childrenField="subjectTimeTable"
          childrenEditable={false}
          childrenShowHeader={true}
          childColumns={[
            { field: "weekDay", header: "Ден от седмицата", sortable: true, filter: true },
            { field: "startHour", header: "Начален час", sortable: true, filter: true },
            { field: "endHour", header: "Краен час", sortable: true, filter: true },
            { field: "evenWeek", header: "Четна седмица", sortable: true, filter: true },
            { field: "startWeek", header: "Начална седмица", sortable: true, filter: true },
            { field: "endWeek", header: "Крайна седмица", sortable: true, filter: true },
            { field: "startDate", header: "Начална дата", sortable: true, filter: true },
            { field: "endDate", header: "Крайна дата", sortable: true, filter: true },
            { field: "comment", header: "Коментар", sortable: true, filter: true },
          ]}
          exportFilename="scheduleSubjects"
          editDialog={editDialog}
          editDialogWidth="90vw"
        />
      )}
    </div>
  );
};

export default ScheduleSubjectsList;
